import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const [flow, setFlow] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const flow = params.get("flow");
    setFlow(flow);
    if (!sessionId) { setState("timeout"); return; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setState("paid");
          return;
        }
      } catch {}
      if (tries.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [checkAuth]);

  return (
    <div className="grain-bg min-h-[calc(100vh-68px)] grid place-items-center px-4">
      <div className="max-w-md w-full rounded-3xl border border-parchment-200 bg-white p-10 text-center" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-forest-700 mx-auto" />
            <h1 className="mt-5 font-serif text-2xl font-semibold text-forest-900">Confirming your payment…</h1>
            <p className="mt-2 text-ink-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="grid place-items-center h-16 w-16 rounded-2xl bg-forest-100 text-forest-800 mx-auto"><CheckCircle2 className="h-9 w-9" /></span>
            <h1 className="mt-5 font-serif text-3xl font-semibold text-forest-900">{flow === "featured" ? "Your listing is featured" : "You're all set"}</h1>
            <p className="mt-2 text-ink-600">{flow === "featured"
              ? "Your attorney profile will now appear at the top of the directory once approved. Thank you!"
              : "Your Case Pass is active for the next 90 days. Every tool is unlocked."}</p>
            <Button onClick={() => navigate(flow === "featured" ? "/attorneys" : "/dashboard")} data-testid="success-continue-btn" className="mt-6 rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50">
              {flow === "featured" ? "View directory" : "Go to my vault"}
            </Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <span className="grid place-items-center h-16 w-16 rounded-2xl bg-[#FFFBEB] text-[#B45309] mx-auto"><XCircle className="h-9 w-9" /></span>
            <h1 className="mt-5 font-serif text-2xl font-semibold text-forest-900">Still processing</h1>
            <p className="mt-2 text-ink-600">Your payment may take a moment to confirm. If it doesn't unlock shortly, refresh your vault or contact support.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50">Go to my vault</Button>
          </>
        )}
      </div>
    </div>
  );
}
